'use client';

import {
  Button,
  DropdownMenuItem,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  useToast,
} from '@ais-ui/ui-core';
import { Bug, Upload, X, Camera } from 'lucide-react';
import * as React from 'react';
import { PiMegaphone } from 'react-icons/pi';
import { useLocalization } from '@ais-ui/locale-management';
import { useProfile } from '@ais-ui/profile';

interface SimpleBugReportData {
  name: string;
  email: string;
  description: string;
  steps: string;
  screenshot?: File;
}

interface BugReportPopoverProps {
  onSubmit?: (data: SimpleBugReportData) => void;
  trigger?: React.ReactNode;
}

export function BugReportPopover({ onSubmit, trigger }: BugReportPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formData, setFormData] = React.useState<SimpleBugReportData>({
    name: '',
    email: '',
    description: '',
    steps: '',
  });
  const { toast } = useToast();
  const [screenshot, setScreenshot] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { messages } = useLocalization();
  const { username } = useProfile();

  const handleInputChange = (
    field: keyof SimpleBugReportData,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleScreenshotUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setScreenshot(file);
    }
  };

  // Jira Service Desk API configuration
  // Requests go through /jira-proxy which handles auth server-side (see apache/jira-proxy.conf)
  // This keeps API credentials secure and handles CORS
  const JIRA_PROXY_URL = '/jira-proxy';
  const SERVICE_DESK_ID = '1'; // SUP project service desk
  const REQUEST_TYPE_ID = '1'; // "Technical support" request type

  /**
   * Creates a service desk request using the Jira Service Desk API.
   * Uses raiseOnBehalfOf to set the customer's email as the reporter.
   * Jira automatically creates the customer account if it doesn't exist.
   */
  const createServiceDeskRequest = async (): Promise<string> => {
    const pageUrl = window.location.href;
    const description = `*Reporter Name:*\n${formData.name}\n\n*Username:*\n${username || 'N/A'}\n\n*Page URL:*\n${pageUrl}\n\n*Description:*\n${formData.description}\n\n*Steps to Reproduce:*\n${formData.steps}`;

    const requestData = {
      serviceDeskId: SERVICE_DESK_ID,
      requestTypeId: REQUEST_TYPE_ID,
      raiseOnBehalfOf: formData.email,
      requestFieldValues: {
        summary: `[Bug Report] ${formData.description.substring(0, 100)}${formData.description.length > 100 ? '...' : ''}`,
        description: description,
      },
    };

    const response = await fetch(
      `${JIRA_PROXY_URL}/rest/servicedeskapi/request`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Jira Service Desk API error:', errorText);
      throw new Error(
        `Failed to create service desk request: ${response.status}`,
      );
    }

    const result = await response.json();
    return result.issueKey;
  };

  /**
   * Attaches a screenshot to an existing Jira issue.
   * Uses the standard Jira REST API for attachments.
   */
  const attachScreenshotToIssue = async (
    issueKey: string,
    file: File,
  ): Promise<void> => {
    const attachmentFormData = new FormData();
    attachmentFormData.append('file', file);

    const response = await fetch(
      `${JIRA_PROXY_URL}/rest/api/3/issue/${issueKey}/attachments`,
      {
        method: 'POST',
        body: attachmentFormData,
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Jira attachment error:', errorText);
      throw new Error(`Failed to attach screenshot: ${response.status}`);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Call custom onSubmit handler if provided
      onSubmit?.({ ...formData, screenshot: screenshot || undefined });

      // Create the service desk request with customer as reporter
      const issueKey = await createServiceDeskRequest();

      // Attach screenshot if provided
      if (screenshot) {
        try {
          await attachScreenshotToIssue(issueKey, screenshot);
        } catch (error) {
          console.error('Failed to attach screenshot:', error);
          // Continue - issue was created, just attachment failed
        }
      }

      toast({
        title: 'Bug report sent!',
        description: `Thank you for your feedback. Issue ${issueKey} has been created.`,
        variant: 'default',
      });

      // Reset form only on success
      setFormData({
        name: '',
        email: '',
        description: '',
        steps: '',
      });
      setScreenshot(null);
      setOpen(false);
    } catch (error) {
      console.error('Bug report submission failed:', error);

      let errorMessage = 'Failed to submit bug report. Please try again later.';

      if (error instanceof Error) {
        if (error.message.includes('credentials not configured')) {
          errorMessage =
            'Bug reporting is not configured. Please contact support.';
        } else if (
          error.message.includes('network') ||
          error.message.includes('fetch')
        ) {
          errorMessage =
            'Network error occurred. Please check your connection and try again.';
        }
      }

      toast({
        title: 'Bug report failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <DropdownMenuItem
          id="sidebar-report-a-bug-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(!open);
          }}
          className="w-full h-full flex items-center group/btn space-x-1 hover:bg-secondary"
        >
          <PiMegaphone className="size-4" />
          <p>Report a bug</p>
        </DropdownMenuItem>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0 ml-2" align="end" side="right">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Bug className="size-5 text-primary" />
            <h3 className="font-semibold text-lg">Report a Bug</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            className="h-6 w-6 p-0"
          >
            <X className="size-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">
              Name
            </Label>
            <Input
              id="name"
              placeholder="First Name Last Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email
            </Label>
            {/* This div is here to combat the html-div that is inserted by password managers */}
            <div>
              <Input
                id="email"
                type="email"
                placeholder="name@mail.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              Description
            </Label>
            <Textarea
              id="description"
              placeholder="The submit button isn't working :("
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              required
            />
          </div>

          {/* Steps to Reproduce */}
          <div className="space-y-2">
            <Label htmlFor="steps" className="text-sm font-medium">
              Steps to Reproduce
            </Label>
            <Textarea
              id="steps"
              placeholder="Example:&#10;1. Go to the login page&#10;2. Enter valid credentials&#10;3. Click submit button"
              value={formData.steps || ''}
              onChange={(e) => handleInputChange('steps', e.target.value)}
              rows={3}
              required
              className="min-h-[100px]"
            />
          </div>

          {/* Screenshot Upload */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Screenshot</Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="gap-2"
              >
                <Upload className="size-4" />
                Upload Screenshot
              </Button>
              {screenshot && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Camera className="size-4" />
                  {screenshot.name}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setScreenshot(null)}
                    className="size-4 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleScreenshotUpload}
              className="hidden"
            />
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <Button
              onClick={handleSubmit}
              disabled={
                !formData.name ||
                !formData.email ||
                !formData.description ||
                !formData.steps ||
                isSubmitting
              }
              className="w-full"
            >
              {isSubmitting ? 'Sending...' : 'Send Bug Report'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
